import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useStore } from "@nanostores/react";
import { $organization } from "@vega-api/custom/stores/organisations.store";
import { useSearchMessagingParticipants } from "@vega-api/generated/coinvest-deal-messaging-participants/coinvest-deal-messaging-participants";
import { useCreateConversation } from "@vega-api/generated/messaging-conversations-v1/messaging-conversations-v1";
import { MessageContentType } from "@vega-api/generated/types/messageContentType";
import { TextareaInput } from "@vega-core/components/Form/Controls/Textarea/TextareaInput";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { trackCoInvestQuestionAsked } from "@vega-core/utils/posthog/coInvestAnalytics";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import {
  COINVEST_MESSAGING_PRODUCT_ID,
  invalidateConversationQueries,
} from "@vega-modules/scenes/CoInvestments/messaging/conversationsData";

type AskQuestionModalProps = {
  onClose: () => void;
  dealId: string;
  dealOpportunityId: string;
  dealName: string;
};

export const AskQuestionModal = ({
  onClose,
  dealId,
  dealOpportunityId,
  dealName,
}: AskQuestionModalProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();
  const organization = useStore($organization);
  const [questionBody, setQuestionBody] = useState("");

  const participantsQuery = useSearchMessagingParticipants(dealOpportunityId);

  const createConversationMutation = useCreateConversation({
    mutation: {
      onSuccess: () => {
        trackCoInvestQuestionAsked({ dealOpportunityId });
        invalidateConversationQueries(queryClient);
        showToast({
          title: m.status_success(),
          description: m.coInvestments_messages_askQuestion_successToast(),
          variant: "success",
        });
        onClose();
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_messages_askQuestion_errorToast(),
        });
      },
    },
  });

  const canSubmit =
    !!questionBody.trim() &&
    !createConversationMutation.isPending &&
    participantsQuery.isSuccess;

  const handleSubmit = () => {
    const participantsData = participantsQuery.data?.data;

    if (!canSubmit || !participantsData) return;

    const participants = [
      ...participantsData.gpUsers,
      ...participantsData.lpUsers,
    ].map((participant) => participant.userId);

    createConversationMutation.mutate({
      data: {
        productId: COINVEST_MESSAGING_PRODUCT_ID,
        meta: {
          dealId,
          dealOpportunityId,
          dealName,
          limitedPartnerName: organization?.name ?? "",
        },
        participants,
        initialMessage: {
          messageContent: questionBody.trim(),
          contentType: MessageContentType.TEXT,
        },
      },
    });
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: handleSubmit,
        mainActionText: m.btn_submit(),
        disabled: !canSubmit,
        secondaryAction: onClose,
        secondaryActionText: m.btn_cancel(),
      }}
      dataTestId="coinvest-ask-question-modal"
      description={m.coInvestments_messages_askQuestion_description()}
      fluidHeight
      open
      setOpen={onClose}
      title={m.coInvestments_messages_askQuestion_title()}
    >
      <TextareaInput
        dataTestId="coinvest-ask-question-input"
        fullWidth
        name="questionBody"
        onChange={(event) => setQuestionBody(event.target.value)}
        placeholder={m.coInvestments_messages_askQuestion_placeholder()}
        rows={8}
        value={questionBody}
        withBorder
      />
    </ActionaryModal>
  );
};
